import React, { useCallback } from 'react';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';

import {
  RedirectionLinksData,
  Session,
  useRedirectionLinks,
  useUpdateRedirectionLinks,
} from '@app/api';
import { InformationBox, Input, SubmitButton } from 'components';

import { useFormikSubmit } from '../../../../hooks/useFormikSubmit';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  twoColumn: {
    display: 'flex',
    gap: theme.marginBase * 4,
  },
  column: {
    flex: 1,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase * 2,
  },
}));

const urlSchema = Yup.string()
  .nullable()
  .transform((val) => val || null);

const settingsValidationSchema = Yup.object().shape({
  finished: urlSchema,
  reported: urlSchema,
  completed: urlSchema,
  applied: urlSchema,
  declined: urlSchema,
  duplicate: urlSchema,
  speeder: urlSchema,
  geoip: urlSchema,
  full: urlSchema,
  notInterested: urlSchema,
  quotaFull: urlSchema,
  wrongDevice: urlSchema,
});

interface Props {
  session: Session;
  sessionLinkId: string;
  onSuccess?: () => any;
}

const EMPTY = {};

export const EditRedirectionLinks = ({ sessionLinkId, onSuccess }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const firstRender = React.useRef(true);

  const { mutateAsync: updateIntegration } =
    useUpdateRedirectionLinks(sessionLinkId);

  const { data: redirectionLinks = EMPTY, isLoading } =
    useRedirectionLinks(sessionLinkId);

  const castedSubmit = useCallback(
    (val: RedirectionLinksData) =>
      updateIntegration(
        settingsValidationSchema.cast(val) as RedirectionLinksData,
      ),
    [updateIntegration],
  );

  const [initialValues, submit] = useFormikSubmit<RedirectionLinksData>(
    castedSubmit,
    redirectionLinks,
    undefined,
    { onSuccess },
  );

  if (isLoading && firstRender.current) {
    return null;
  }
  firstRender.current = false;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submit}
      validationSchema={settingsValidationSchema}
    >
      <Form>
        <div className={classes.twoColumn}>
          <div className={classes.column}>
            <Input
              label={t('editSession.admin.redirectionLinks.applied')}
              name="applied"
              small
            />
            <Input
              label={t('editSession.admin.redirectionLinks.finished')}
              name="finished"
              small
            />
            <Input
              label={t('editSession.admin.redirectionLinks.completed')}
              name="completed"
              small
            />
            <Input
              label={t('editSession.admin.redirectionLinks.full')}
              name="full"
              small
            />
            <Input
              label={t('editSession.admin.redirectionLinks.notInterested')}
              name="notInterested"
              small
            />
            <Input
              label={t('editSession.admin.redirectionLinks.quotaFull')}
              name="quotaFull"
              small
            />
          </div>
          <div className={classes.column}>
            <Input
              label={t('editSession.admin.redirectionLinks.reported')}
              name="reported"
              small
            />
            <Input
              label={t('editSession.admin.redirectionLinks.declined')}
              name="declined"
              small
            />
            <Input
              label={t('editSession.admin.redirectionLinks.duplicate')}
              name="duplicate"
              small
            />
            <Input
              label={t('editSession.admin.redirectionLinks.speeder')}
              name="speeder"
              small
            />
            <Input
              label={t('editSession.admin.redirectionLinks.geoip')}
              name="geoip"
              small
            />
            <Input
              label={t('editSession.admin.redirectionLinks.wrongDevice')}
              name="wrongDevice"
              small
            />
          </div>
        </div>
        <InformationBox
          text={t('editSession.admin.redirectionLinks.replacedStrings')}
        />
        <div className={classes.buttonContainer}>
          <SubmitButton
            small
            text={t('editSession.admin.redirectionLinks.save')}
          />
        </div>
      </Form>
    </Formik>
  );
};
